import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { firstValueFrom } from 'rxjs';
import { UnitConversionsService } from '../../../../../../services/api/generated/unit-conversions/unit-conversions.service';
import { UnitsService } from '../../../../../../services/api/generated/units/units.service';
import { GraphMeasure } from '../../../../../../types/graph-data.interface';
import { GraphTypeId } from '../../../../../../types/graph.interface';
import { CustomScaleConfig } from '../../../../../../types/graphic-metadata.interface';
import { MeasureConfig, MeasureConfigResult } from '../../../../../../types/measure-config.interface';
import { Unit } from '../../../../../../types/unit.interface';
import { formatUnitLabel } from '../../../../../../utils/unit-select.utils';
import { validateNumberInRange } from '../../../../../../utils/validators';
import { CesDeleteButtonComponent } from '../../../../../ui/ces-buttons/ces-delete-button.component';
import { CesDialogFooterComponent } from '../../../../../ui/dialog-footer/dialog-footer.component';

type UnitOption = { label: string; value: string };

interface MeasureConfigDialogData {
  measure: GraphMeasure;
  config?: MeasureConfig;
  graphType?: GraphTypeId;
  otherMeasures?: GraphMeasure[];
  customScales?: Map<number, CustomScaleConfig>;
  measureConfigs?: Map<number, MeasureConfig>;
}

const MIN_SCALE_LIMIT = -1000000;
const MAX_SCALE_LIMIT = 1000000;

function customScaleValidator(control: AbstractControl): ValidationErrors | null {
  const customScale = control.get('customScale')?.value as unknown;
  if (!customScale) return null;

  const min: unknown = control.get('scaleMin')?.value;
  const max: unknown = control.get('scaleMax')?.value;

  const minError = validateNumberInRange(min, MIN_SCALE_LIMIT, MAX_SCALE_LIMIT);
  if (minError) return minError;

  const maxError = validateNumberInRange(max, MIN_SCALE_LIMIT, MAX_SCALE_LIMIT);
  if (maxError) return maxError;

  const hasMin = min !== null && min !== undefined && min !== '';
  const hasMax = max !== null && max !== undefined && max !== '';

  if (hasMin && hasMax && Number(min) >= Number(max)) {
    return { scaleRange: true };
  }

  return null;
}

@Component({
  selector: 'ces-measure-config-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.enter)': 'onEnterKey($event)',
  },
  imports: [
    ReactiveFormsModule,
    TranslocoModule,
    SelectModule,
    InputTextModule,
    ToggleSwitchModule,
    CesDeleteButtonComponent,
    CesDialogFooterComponent,
  ],
  templateUrl: './measure-config-dialog.component.html',
})
export class MeasureConfigDialogComponent {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly config = inject<DynamicDialogConfig<MeasureConfigDialogData>>(DynamicDialogConfig);
  private readonly fb = inject(FormBuilder);
  private readonly conversionApi = inject(UnitConversionsService);
  private readonly unitApi = inject(UnitsService);
  private readonly translate = inject(TranslocoService);

  private readonly data: MeasureConfigDialogData = this.requireData();

  readonly measure: GraphMeasure = this.data.measure;
  private readonly initialConfig = this.data.config;
  private readonly graphType = this.data.graphType;
  private readonly otherMeasures = this.data.otherMeasures ?? [];
  private readonly existingScales = this.data.customScales ?? new Map<number, CustomScaleConfig>();
  private readonly existingConfigs = this.data.measureConfigs ?? new Map<number, MeasureConfig>();

  private requireData(): MeasureConfigDialogData {
    const data = this.config.data;
    if (!data) {
      throw new Error('MeasureConfigDialogComponent requires DynamicDialogConfig.data');
    }
    return data;
  }

  readonly showPolynomialOptions = this.graphType === 1;

  readonly unitOptions = signal<UnitOption[]>([]);
  readonly isLoadingUnits = signal(true);
  private loadingStarted = false;

  private readonly resolvedInitialLabel = this.resolveInitialLabel();
  private readonly hadLabelOnOpen = !!this.resolvedInitialLabel;

  private resolveInitialLabel(): string {
    if (this.initialConfig?.scaleLabel === null) return '';
    if (this.initialConfig?.scaleLabel) return this.initialConfig.scaleLabel;

    if (this.initialConfig?.customScale) {
      const currentSignature = `${this.measure.unit}|${this.initialConfig.scaleMin ?? 'auto'}|${this.initialConfig.scaleMax ?? 'auto'}`;

      for (const otherMeasure of this.otherMeasures) {
        const otherScale = this.existingScales.get(otherMeasure.id);
        if (!otherScale?.enabled) continue;

        const otherSignature = `${otherMeasure.unit}|${otherScale.min ?? 'auto'}|${otherScale.max ?? 'auto'}`;
        if (currentSignature === otherSignature && otherScale.label?.trim()) {
          return otherScale.label.trim();
        }
      }
      return '';
    }

    for (const otherMeasure of this.otherMeasures) {
      if (otherMeasure.unit !== this.measure.unit) continue;

      const otherScale = this.existingScales.get(otherMeasure.id);
      if (otherScale?.enabled) continue;

      const otherConfig = this.existingConfigs.get(otherMeasure.id);
      if (otherConfig?.scaleLabel?.trim()) return otherConfig.scaleLabel.trim();
    }

    return '';
  }

  readonly form = this.fb.group(
    {
      unit: [this.initialConfig?.unit ?? undefined],
      polynomInferieur: [this.initialConfig?.polynomInferieur ?? false],
      polynomSuperieur: [this.initialConfig?.polynomSuperieur ?? false],
      customScale: [this.initialConfig?.customScale ?? false],
      scaleMin: [this.initialConfig?.scaleMin as number | null],
      scaleMax: [this.initialConfig?.scaleMax as number | null],
      scaleLabel: [this.resolvedInitialLabel],
    },
    { validators: customScaleValidator, updateOn: 'change' },
  );

  readonly isCustomScale = toSignal(this.form.controls.customScale.valueChanges, {
    initialValue: this.initialConfig?.customScale ?? false,
  });

  private readonly currentScaleMin = toSignal(this.form.controls.scaleMin.valueChanges, {
    initialValue: this.initialConfig?.scaleMin ?? null,
  });

  private readonly currentScaleMax = toSignal(this.form.controls.scaleMax.valueChanges, {
    initialValue: this.initialConfig?.scaleMax ?? null,
  });

  private readonly currentScaleLabel = toSignal(this.form.controls.scaleLabel.valueChanges, {
    initialValue: this.resolvedInitialLabel,
  });

  readonly sharedAxisInfo = computed(() => {
    const currentLabel = this.currentScaleLabel()?.trim() ?? '';
    const isCustom = this.isCustomScale();

    if (isCustom) {
      const min = this.currentScaleMin();
      const max = this.currentScaleMax();
      const currentSignature = `${this.measure.unit}|${min ?? 'auto'}|${max ?? 'auto'}`;
      const sharedMeasures: { id: number; name: string; label: string }[] = [];

      for (const otherMeasure of this.otherMeasures) {
        const otherScale = this.existingScales.get(otherMeasure.id);
        if (!otherScale?.enabled) continue;

        const otherSignature = `${otherMeasure.unit}|${otherScale.min ?? 'auto'}|${otherScale.max ?? 'auto'}`;
        if (currentSignature === otherSignature) {
          sharedMeasures.push({
            id: otherMeasure.id,
            name: otherMeasure.name,
            label: otherScale.label?.trim() ?? '',
          });
        }
      }

      const firstShared = sharedMeasures[0];
      if (!firstShared) return null;

      const existingLabel = firstShared.label;
      const labelWillChange = currentLabel !== '' && currentLabel !== existingLabel;
      const labelWillClear = currentLabel === '' && existingLabel !== '';

      return {
        measureNames: sharedMeasures.map(m => m.name),
        measureIds: sharedMeasures.map(m => m.id),
        existingLabel,
        labelWillChange,
        labelWillClear,
        message: this.translate.translate('graphs.measureConfig.sharedAxis', { measures: sharedMeasures.map(m => m.name).join(', ') }),
      };
    }

    const sameUnitMeasures = this.otherMeasures.filter(m => {
      const hasCustomScale = this.existingScales.get(m.id)?.enabled;
      return !hasCustomScale && m.unit === this.measure.unit;
    });

    if (sameUnitMeasures.length === 0) return null;

    const existingLabels = sameUnitMeasures
      .map(m => this.existingConfigs.get(m.id)?.scaleLabel?.trim())
      .filter((l): l is string => !!l);

    const existingLabel = existingLabels[0] ?? '';
    const labelWillChange = currentLabel !== '' && currentLabel !== existingLabel;
    const labelWillClear = currentLabel === '' && existingLabel !== '';

    return {
      measureNames: sameUnitMeasures.map(m => m.name),
      measureIds: sameUnitMeasures.map(m => m.id),
      existingLabel,
      labelWillChange,
      labelWillClear,
      message: this.translate.translate('graphs.measureConfig.sharedUnitAxis', { unit: this.measure.unit }),
    };
  });

  constructor() {
    void this.loadUnitOptions();
  }

  private async loadUnitOptions(): Promise<void> {
    if (this.loadingStarted) {
      return;
    }
    this.loadingStarted = true;
    this.isLoadingUnits.set(true);

    try {
      const sourceUnits = await firstValueFrom(this.unitApi.getUnitListResource<Unit[]>({ measureId: this.measure.id }));
      const sourceUnit = sourceUnits?.[0];
      if (!sourceUnit) {
        // Mesure sans unité : proposer toutes les unités connues plutôt qu'une liste figée.
        this.unitOptions.set(await this.loadAllUnitOptions());
        this.isLoadingUnits.set(false);
        return;
      }
      const conversions = await firstValueFrom(
        this.conversionApi.getUnitConversionDomainResource({ sourceUnitIds: String(sourceUnit.id) }),
      );

      const unitOpts: UnitOption[] = [{ label: formatUnitLabel(sourceUnit.name, sourceUnit.symbol), value: sourceUnit.symbol }];

      if (conversions?.length) {
        conversions.forEach(conv => {
          unitOpts.push({ label: formatUnitLabel(conv.tgtUnitName, conv.tgtSymbol), value: conv.tgtSymbol });
        });
      }

      this.unitOptions.set(unitOpts);

      if (!this.initialConfig?.unit && sourceUnit.symbol) {
        this.form.patchValue({ unit: sourceUnit.symbol });
      }
    } catch (error) {
      console.error('Failed to load unit options:', error);
      this.unitOptions.set([]);
    } finally {
      this.isLoadingUnits.set(false);
    }
  }

  private async loadAllUnitOptions(): Promise<UnitOption[]> {
    const units = await firstValueFrom(this.unitApi.getUnitListResource<Unit[]>());
    return (units ?? [])
      .map(unit => ({ label: formatUnitLabel(unit.name, unit.symbol), value: unit.symbol }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }

  private readonly formStatus = toSignal(this.form.statusChanges, {
    initialValue: this.form.status,
  });

  readonly isFormValid = computed(() => {
    this.formStatus();
    return this.form.valid;
  });

  readonly hasScaleError = computed(() => {
    this.formStatus();
    const errors = this.form.errors;
    if (!errors) return false;
    const hasError = Boolean(errors['scaleRange']) || Boolean(errors['invalidNumber']) || Boolean(errors['outOfRange']);
    return this.isCustomScale() && hasError;
  });

  readonly scaleErrorMessage = computed(() => {
    if (!this.hasScaleError()) return '';
    const errors = this.form.errors;
    if (errors?.['invalidNumber']) {
      return this.translate.translate('graphs.measureConfig.validation.invalidNumber');
    }
    if (errors?.['outOfRange']) {
      return this.translate.translate('graphs.measureConfig.validation.outOfRange');
    }
    if (errors?.['scaleRange']) {
      return this.translate.translate('graphs.measureConfig.validation.minLessThanMax');
    }
    return '';
  });

  readonly scaleInputClass = computed(() =>
    this.hasScaleError()
      ? 'w-full !border-red-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-800'
      : 'w-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--p-primary-color)]',
  );

  onEnterKey(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    if (target.tagName === 'TEXTAREA') return;

    event.preventDefault();
    if (this.form.valid) {
      this.onValidate();
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onRemove(): void {
    this.dialogRef.close({ action: 'remove' });
  }

  onValidate(): void {
    if (!this.form.valid) return;

    const { unit, polynomInferieur, polynomSuperieur, customScale, scaleMin, scaleMax, scaleLabel } = this.form.value;
    const sharedInfo = this.sharedAxisInfo();
    const labelProvided = !!scaleLabel?.trim();
    const labelCleared = this.hadLabelOnOpen && !labelProvided;

    const resolvedLabel = this.resolveScaleLabel(scaleLabel, labelCleared);
    const result: MeasureConfigResult = {
      action: 'save',
      config: {
        ...(unit != null && { unit }),
        polynomInferieur: polynomInferieur ?? false,
        polynomSuperieur: polynomSuperieur ?? false,
        customScale: customScale ?? false,
        ...(scaleMin != null && { scaleMin }),
        ...(scaleMax != null && { scaleMax }),
        ...(resolvedLabel !== undefined && { scaleLabel: resolvedLabel }),
      },
      ...((labelProvided || labelCleared) && sharedInfo?.measureIds.length ? { sharedMeasureIds: sharedInfo.measureIds } : {}),
    };
    this.dialogRef.close(result);
  }

  private resolveScaleLabel(scaleLabel: string | null | undefined, labelCleared: boolean): string | null | undefined {
    const trimmed = scaleLabel?.trim();
    if (trimmed) return trimmed;
    // Un libellé effacé et déjà enregistré (null) le reste tant que le champ reste
    // vide ; sans lui, le rouvrir puis valider supprimerait l'effacement enregistré.
    return labelCleared || this.initialConfig?.scaleLabel === null ? null : undefined;
  }
}
